import React, { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw, ShieldAlert } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { Button } from '../../components/ui/button';

export default function PlatformAlertsPage() {
  const { api } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/platform/assist/alerts');
      setAlerts(data.alerts || []);
    } catch (err) {
      console.error('Failed to load platform alerts', err);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAlerts();
    const timer = setInterval(loadAlerts, 30000);
    return () => clearInterval(timer);
  }, []);

  const severityClass = (severity) => {
    if (severity === 'critical') {
      return 'border-red-500/40 bg-red-500/10 text-red-300';
    }

    if (severity === 'warning') {
      return 'border-amber-500/40 bg-amber-500/10 text-amber-300';
    }

    return 'border-blue-500/30 bg-blue-500/10 text-blue-300';
  };

  return (
    <div className="p-6 space-y-6" data-testid="platform-alerts-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-amber-500" />
            Platform Alerts
          </h1>

          <p className="text-sm text-zinc-500 mt-1">
            Cross-tenant operational alerts and support signals.
          </p>
        </div>

        <Button
          variant="outline"
          onClick={loadAlerts}
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-[#101014] overflow-hidden">
        <div className="border-b border-zinc-800 px-4 py-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-white">
            Alert Feed
          </div>

          <div className="text-xs text-zinc-500">
            {alerts.length} alert{alerts.length === 1 ? '' : 's'}
          </div>
        </div>

        <div className="p-4 space-y-3">
          {loading && (
            <div className="text-sm text-zinc-500 py-8 text-center">
              Loading alerts...
            </div>
          )}

          {!loading && alerts.length === 0 && (
            <div className="text-sm text-zinc-500 py-8 text-center">
              No platform alerts detected.
            </div>
          )}

          {!loading && alerts.map((alert, idx) => (
            <div
              key={`${alert.type || 'alert'}-${idx}`}
              className={`rounded-xl border p-4 ${severityClass(alert.severity)}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    <h3 className="text-sm font-bold">
                      {alert.title || 'Platform Alert'}
                    </h3>
                  </div>

                  <p className="text-xs mt-2 opacity-90">
                    {alert.message || 'No details supplied.'}
                  </p>
                </div>

                <span className="rounded-full border border-current px-2 py-0.5 text-[10px] uppercase">
                  {alert.severity || 'info'}
                </span>
              </div>

              <div className="mt-3 text-[11px] opacity-70">
                {alert.site_name || alert.tenant_id || 'Platform'}
                {alert.created_at ? ` · ${new Date(alert.created_at).toLocaleString('en-AU')}` : ''}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
